'use client';

import { AudioLinesIcon, X } from 'lucide-react';
import Image, { type StaticImageData } from 'next/image';
import { type ComponentType, type ReactNode } from 'react';

import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import {
  DialogDrawer,
  DialogDrawerClose,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { TypographyH3, TypographyP } from '@heidi/design-system/typography';

export interface ScribeOnboardingFeature {
  icon: ComponentType<{ className?: string }>;
  text: string;
}

interface ScribeOnboardingModalLayoutProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  dismissible: boolean;
  title: string;
  subtitle: string;
  features: ScribeOnboardingFeature[];
  /**
   * Right-pane preview rendered as-is (e.g. an inline SVG component carrying
   * its own accessible label). Takes precedence over `imageSrc`.
   */
  image?: ReactNode;
  /**
   * Right-pane preview image rendered through `next/image`. Accepts both a
   * URL string and a `StaticImageData` because static asset imports are typed
   * differently in custom.d.ts (`*.svg` → string, `*.png` → StaticImageData)
   * even though Next returns StaticImageData for both at runtime.
   */
  imageSrc?: string | StaticImageData;
  imageAlt?: string;
  /** Optional class for each feature label (e.g. `font-medium` to match a design). */
  featureTextClassName?: string;
  /**
   * `intrinsic` — natural asset size with offset padding (default onboarding modal).
   * `cover` — scale to pane height, top-left anchored (dictate onboarding visual).
   */
  imageFit?: 'intrinsic' | 'cover';
  /** CTA button(s) rendered at the bottom of the left pane. */
  children: ReactNode;
}

/**
 * Shared two-pane chrome for the Scribe onboarding modals: framed icon →
 * heading → subtitle → feature list → CTA slot, with a preview image on the
 * right. Presentational only — each modal owns its queue subscription,
 * analytics, copy, and CTA wiring.
 */
export const ScribeOnboardingModalLayout = ({
  isOpen,
  onOpenChange,
  dismissible,
  title,
  subtitle,
  features,
  image,
  imageSrc,
  imageAlt,
  featureTextClassName,
  imageFit = 'intrinsic',
  children,
}: ScribeOnboardingModalLayoutProps) => {
  return (
    <DialogDrawer open={isOpen} onOpenChange={onOpenChange}>
      <DialogDrawerContent
        className="max-h-[542px] max-w-[940px] overflow-visible border-0 bg-transparent p-0 shadow-none"
        overlayClassName="backdrop-blur-[3px]"
        hideCloseIcon={true}
        dismissible={dismissible}
      >
        <div className="relative flex flex-col overflow-hidden rounded-2xl bg-surface sm:flex-row">
          {/* Close button - positioned inside modal */}
          <DialogDrawerClose asChild>
            <Button
              iconOnly
              variant="ghost"
              size="sm"
              className="absolute end-2 top-2 z-50 cursor-pointer border-0 bg-transparent p-0"
            >
              <X />
            </Button>
          </DialogDrawerClose>

          {/* Left Pane - Promotional Content */}
          <div className="z-10 flex flex-1 flex-col gap-6 bg-surface px-8 py-10">
            {/* Icon */}
            <div className="flex items-start">
              <div className="flex size-10 items-center justify-center rounded-lg bg-surface-accent">
                <AudioLinesIcon className="size-5 text-text-secondary" />
              </div>
            </div>

            {/* Heading */}
            <div className="flex flex-col gap-3">
              <TypographyH3 className="font-exposure text-3xl leading-tight">
                {title}
              </TypographyH3>
              <TypographyP className="text-text-secondary">{subtitle}</TypographyP>
            </div>

            {/* Feature List */}
            <div className="flex flex-col gap-4">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.text} className="flex items-center gap-4">
                    <Icon className="size-5 shrink-0 text-text-secondary" />
                    <TypographyP className={cn(featureTextClassName)}>
                      {feature.text}
                    </TypographyP>
                  </div>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-2 pt-6">{children}</div>
          </div>

          {/* Right Pane - overlapping demo preview */}
          <div
            className={cn(
              'hidden overflow-hidden bg-surface-accent',
              imageFit === 'cover'
                ? 'relative min-h-0 flex-1 ps-6 sm:block'
                : 'items-center justify-center ps-6 pt-6 shadow-lg sm:flex'
            )}
          >
            {image ??
              (imageSrc !== undefined && (
                <Image
                  src={imageSrc}
                  alt={imageAlt ?? ''}
                  priority
                  {...(imageFit === 'cover'
                    ? {
                        fill: true,
                        className: 'object-cover object-left-top',
                        sizes: '(min-width: 640px) 470px, 0px',
                      }
                    : {})}
                />
              ))}
          </div>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
